:host {
  /**
   * @prop --background: Background of the textarea
   *
   * @prop --border-radius: Border radius of the textarea
   * @prop --border-color: Color of the border below the textarea when using helper text, error text, or counter
   * @prop --border-radius: Radius of the textarea border. A large radius may display unevenly when using fill="outline"; if needed, use shape="round" instead or increase --padding-start.
   * @prop --border-style: Style of the border below the textarea when using helper text, error text, or counter
   * @prop --border-width: Width of the border below the textarea when using helper text, error text, or counter
   *
   * @prop --color: Color of the text
   *
   * @prop --placeholder-color: Color of the placeholder text
   * @prop --placeholder-font-style: Style of the placeholder text
   * @prop --placeholder-font-weight: Weight of the placeholder text
   * @prop --placeholder-opacity: Opacity of the placeholder text
   *
   * @prop --highlight-color-focused: The color of the highlight on the textarea when focused
   * @prop --highlight-color-valid: The color of the highlight on the textarea when valid
   * @prop --highlight-color-invalid: The color of the highlight on the textarea when invalid
   *
   * @prop --padding-top: Top padding of the textarea
   * @prop --padding-end: Right padding if direction is left-to-right, and left padding if direction is right-to-left of the textarea
   * @prop --padding-bottom: Bottom padding of the textarea
   * @prop --padding-start: Left padding if direction is left-to-right, and right padding if direction is right-to-left of the textarea
   */
  --background: initial;
  --color: initial;
  --placeholder-color: initial;
  --placeholder-font-style: initial;
  --placeholder-font-weight: initial;
  --placeholder-opacity: 0.6;
  --padding-top: 0;
  --padding-end: 0;
  --padding-bottom: 0;
  --padding-start: 0;
  --border-radius: 0;
  --border-style: solid;
  --highlight-color-focused: var(--ion-color-primary, #3880ff);
  --highlight-color-valid: var(--ion-color-success, #2dd36f);
  --highlight-color-invalid: var(--ion-color-danger, #eb445a);
  /**
   * This is a private API that is used to switch
   * out the highlight color based on the state
   * of the component without having to write
   * different selectors for different fill variants.
   */
  --highlight-color: var(--highlight-color-focused);
  display: block;
  position: relative;
  width: 100%;
  color: var(--color);
  font-family: var(--ion-font-family, inherit);
  z-index: 2;
  box-sizing: border-box;
}

:host(.legacy-textarea) {
  flex: 1;
  background: var(--background);
  white-space: pre-wrap;
}

:host(.legacy-textarea.ion-color) {
  color: var(--ion-color-base);
}

:host(:not(.legacy-textarea)) {
  --padding-bottom: 8px;
}

:host(.ion-color) {
  --highlight-color-focused: var(--ion-color-base);
  background: initial;
}

:host-context(ion-item) {
  align-self: baseline;
}

:host-context(ion-item:not(.item-label)) {
  --padding-start: 0;
}

:host-context(ion-item)[slot=start],
:host-context(ion-item)[slot=end] {
  width: auto;
}

.native-textarea {
  margin-left: 0;
  margin-right: 0;
  margin-top: 0;
  margin-bottom: 0;
  padding-left: 0;
  padding-right: 0;
  padding-top: 0;
  padding-bottom: 0;
  display: block;
  position: relative;
  flex: 1;
  width: 100%;
  max-width: 100%;
  max-height: 100%;
  border: 0;
  outline: none;
  background: transparent;
  white-space: pre-wrap;
  /**
   * This ensures the textarea
   * remains on top of any decoration
   * that we render (particularly the
   * outline border when fill="outline").
   * If we did not do this then Axe would
   * be unable to determine the color
   * contrast of the textarea.
   */
  z-index: 1;
  box-sizing: border-box;
  resize: none;
  appearance: none;
}
.native-textarea::placeholder {
  padding-left: 0;
  padding-right: 0;
  padding-top: 0;
  padding-bottom: 0;
  color: var(--placeholder-color);
  font-family: inherit;
  font-style: var(--placeholder-font-style);
  font-weight: var(--placeholder-font-weight);
  opacity: var(--placeholder-opacity);
}

:host(.legacy-textarea) .native-textarea {
  white-space: inherit;
}

:host(.legacy-textarea) .native-textarea,
:host(.legacy-textarea) .textarea-legacy-wrapper::after {
  -webkit-padding-start: var(--padding-start);
  padding-inline-start: var(--padding-start);
  -webkit-padding-end: var(--padding-end);
  padding-inline-end: var(--padding-end);
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);
  border-radius: var(--border-radius);
}

.native-textarea {
  color: inherit;
  font-family: inherit;
  font-size: inherit;
  font-style: inherit;
  font-weight: inherit;
  letter-spacing: inherit;
  text-align: inherit;
  text-decoration: inherit;
  text-indent: inherit;
  text-overflow: inherit;
  text-transform: inherit;
  grid-area: 1/1/2/2;
  word-break: break-word;
}

:host(.legacy-textarea) .textarea-legacy-wrapper::after {
  font-family: inherit;
  font-size: inherit;
  font-style: inherit;
  font-weight: inherit;
  letter-spacing: inherit;
  text-decoration: inherit;
  text-indent: inherit;
  text-overflow: inherit;
  text-transform: inherit;
  text-align: inherit;
  white-space: inherit;
  color: inherit;
  grid-area: 1/1/2/2;
  word-break: break-word;
}

.cloned-input {
  top: 0;
  bottom: 0;
  position: absolute;
  pointer-events: none;
}
@supports (inset-inline-start: 0) {
  .cloned-input {
    inset-inline-start: 0;
  }
}
@supports not (inset-inline-start: 0) {
  .cloned-input {
    left: 0;
  }
  :host-context([dir=rtl]) .cloned-input {
    left: unset;
    right: unset;
    right: 0;
  }
  [dir=rtl] .cloned-input {
    left: unset;
    right: unset;
    right: 0;
  }
  @supports selector(:dir(rtl)) {
    .cloned-input:dir(rtl) {
      left: unset;
      right: unset;
      right: 0;
    }
  }
}

/**
 * The cloned input needs to be disabled on
 * Android otherwise the viewport will still
 * shift when running scroll assist.
 */
.cloned-input:disabled {
  opacity: 1;
}

:host(.legacy-textarea[auto-grow]) .cloned-input {
  margin-left: 0;
  margin-right: 0;
  margin-top: 0;
  margin-bottom: 0;
}

:host([auto-grow]) .cloned-input {
  height: 100%;
}

:host([auto-grow]) .native-textarea {
  overflow: hidden;
}

:host-context(.item-label-floating.item-has-placeholder:not(.item-has-value)) {
  opacity: 0;
}

:host-context(.item-label-floating.item-has-placeholder:not(.item-has-value).item-has-focus) {
  transition: opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 1;
}

.textarea-wrapper {
  -webkit-padding-start: var(--padding-start);
  padding-inline-start: var(--padding-start);
  -webkit-padding-end: var(--padding-end);
  padding-inline-end: var(--padding-end);
  padding-top: 0px;
  padding-bottom: 0px;
  border-radius: var(--border-radius);
  display: flex;
  position: relative;
  flex-grow: 1;
  align-items: flex-start;
  height: inherit;
  min-height: inherit;
  transition: background-color 15ms linear;
  background: var(--background);
  line-height: normal;
}

.native-wrapper {
  display: flex;
  position: relative;
  flex-grow: 1;
  width: 100%;
  height: 100%;
}

:host(.has-focus) textarea {
  caret-color: var(--highlight-color);
}

.native-wrapper textarea {
  -webkit-padding-start: 0px;
  padding-inline-start: 0px;
  -webkit-padding-end: 0px;
  padding-inline-end: 0px;
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);
}

.native-wrapper,
.textarea-legacy-wrapper {
  display: grid;
  min-width: inherit;
  max-width: inherit;
  min-height: inherit;
  max-height: inherit;
  /**
   * This avoids a WebKit bug where
   * the height of the inner textarea
   * is incorrect and flows outside the
   * parent container: https://bugs.webkit.org/show_bug.cgi?id=256781
   * TODO FW-4734
   */
  grid-auto-rows: 100%;
}
.native-wrapper::after,
.textarea-legacy-wrapper::after {
  white-space: pre-wrap;
  content: attr(data-replicated-value) " ";
  visibility: hidden;
}

.native-wrapper::after {
  padding-left: 0;
  padding-right: 0;
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);
  margin-left: 0;
  margin-right: 0;
  margin-top: 0;
  margin-bottom: 0;
  border-radius: var(--border-radius);
  /**
   * Note: Do not use @include text-inherit()
   * as that sets white-space: inherit
   * Instead, we use white-space: pre-wrap above.
   */
  color: inherit;
  font-family: inherit;
  font-size: inherit;
  font-style: inherit;
  font-weight: inherit;
  letter-spacing: inherit;
  text-align: inherit;
  text-decoration: inherit;
  text-indent: inherit;
  text-overflow: inherit;
  text-transform: inherit;
  grid-area: 1/1/2/2;
  word-break: break-word;
}

:host(.ion-touched.ion-invalid) {
  --highlight-color: var(--highlight-color-invalid);
}

/**
 * The component highlight is only shown
 * on focus, so we can safely set the valid
 * color state when touched/valid. If we
 * set it when .has-focus is present then
 * the highlight color would change
 * from the valid color to the component's
 * color during the transition after the
 * component loses focus.
 */
:host(.ion-valid) {
  --highlight-color: var(--highlight-color-valid);
}

.textarea-bottom {
  /**
   * The bottom content should take on the start and end
   * padding so it is always aligned with either the label
   * or the start of the textarea.
   */
  -webkit-padding-start: var(--padding-start);
  padding-inline-start: var(--padding-start);
  -webkit-padding-end: var(--padding-end);
  padding-inline-end: var(--padding-end);
  padding-top: 5px;
  padding-bottom: 0;
  display: flex;
  justify-content: space-between;
  border-top: var(--border-width) var(--border-style) var(--border-color);
  font-size: 12px;
}

/**
 * If the textarea has a validity state, the
 * border and label should reflect that as a color.
 */
:host(.has-focus.ion-valid),
:host(.ion-touched.ion-invalid) {
  --border-color: var(--highlight-color);
}

/**
 * Error text should only be shown when .ion-invalid is
 * present on the textarea. Otherwise the helper text should
 * be shown.
 */
.textarea-bottom .error-text {
  display: none;
  color: var(--highlight-color-invalid);
}

.textarea-bottom .helper-text {
  display: block;
  color: var(--ion-color-step-550, #737373);
}

:host(.ion-touched.ion-invalid) .textarea-bottom .error-text {
  display: block;
}

:host(.ion-touched.ion-invalid) .textarea-bottom .helper-text {
  display: none;
}

.textarea-bottom .counter {
  /**
   * Counter should always be at
   * the end of the container even
   * when no helper/error texts are used.
   */
  -webkit-margin-start: auto;
  margin-inline-start: auto;
  color: var(--ion-color-step-550, #737373);
  white-space: nowrap;
  padding-inline-start: 16px;
}

.label-text-wrapper {
  -webkit-padding-start: 0px;
  padding-inline-start: 0px;
  -webkit-padding-end: 0px;
  padding-inline-end: 0px;
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);
  /**
   * Label text should not extend
   * beyond the bounds of the textarea.
   * However, we do not set the max
   * width to 100% because then
   * only the label would show and users
   * would not be able to see what they are typing.
   */
  max-width: 200px;
  transition: color 150ms cubic-bezier(0.4, 0, 0.2, 1), transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
  /**
   * This ensures that double tapping this text
   * clicks the <label> and focuses the textarea
   * when a screen reader is enabled.
   */
  pointer-events: none;
}

/**
 * We need to use two elements instead of
 * one. The .label-text-wrapper is responsible
 * for centering the label text vertically regardless
 * of the textarea height using flexbox.
 *
 * The .label-text element is responsible for controlling
 * overflow when label-placement="fixed".
 * We want the ellipses to show up when the
 * fixed label overflows, but text-overflow: ellipsis only
 * works on block-level elements. A flex item is
 * considered blockified (https://www.w3.org/TR/css-display-3/#blockify).
 */
.label-text,
::slotted([slot=label]) {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

/**
 * If no label text is placed into the slot
 * then the element should be hidden otherwise
 * there will be additional margins added.
 */
.label-text-wrapper-hidden,
.textarea-outline-notch-hidden {
  display: none;
}

.textarea-wrapper textarea {
  /**
   * When the floating label appears on top of the
   * textarea, we need to fade the textarea out so that the
   * label does not overlap with the placeholder.
   */
  transition: opacity 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

/**
 * Label is on the left of the textarea in LTR and
 * on the right in RTL.
 */
:host(.textarea-label-placement-start) .textarea-wrapper {
  flex-direction: row;
}

:host(.textarea-label-placement-start) .label-text-wrapper {
  /**
   * The margin between the label and
   * the textarea should be on the end
   * when the label sits at the start.
   */
  -webkit-margin-start: 0;
  margin-inline-start: 0;
  -webkit-margin-end: 16px;
  margin-inline-end: 16px;
  margin-top: 0;
  margin-bottom: 0;
}

/**
 * Label is on the right of the textarea in LTR and
 * on the left in RTL.
 */
:host(.textarea-label-placement-end) .textarea-wrapper {
  flex-direction: row-reverse;
}

/**
 * The margin between the label and
 * the textarea should be on the start
 * when the label sits at the end.
 */
:host(.textarea-label-placement-end) .label-text-wrapper {
  -webkit-margin-start: 16px;
  margin-inline-start: 16px;
  -webkit-margin-end: 0;
  margin-inline-end: 0;
  margin-top: 0;
  margin-bottom: 0;
}

:host(.textarea-label-placement-fixed) .label-text-wrapper {
  /**
   * The margin between the label and
   * the textarea should be on the end
   * when the label sits at the start.
   */
  -webkit-margin-start: 0;
  margin-inline-start: 0;
  -webkit-margin-end: 16px;
  margin-inline-end: 16px;
  margin-top: 0;
  margin-bottom: 0;
}

/**
 * Label is on the left of the textarea in LTR and
 * on the right in RTL. Label also has a fixed width.
 */
:host(.textarea-label-placement-fixed) .label-text {
  flex: 0 0 100px;
  width: 100px;
  min-width: 100px;
  max-width: 200px;
}

/**
 * Stacked: Label sits above the textarea and is scaled down.
 * Floating: Label sits over the textarea when the textarea has no
 * value and is blurred. Label sits above the textarea and is scaled
 * down when the textarea is focused or has a value.
 *
 */
:host(.textarea-label-placement-stacked) .textarea-wrapper,
:host(.textarea-label-placement-floating) .textarea-wrapper {
  flex-direction: column;
  align-items: start;
}

/**
 * Ensures that the label animates
 * up and to the left in LTR or
 * up and to the right in RTL.
 */
:host(.textarea-label-placement-stacked) .label-text-wrapper,
:host(.textarea-label-placement-floating) .label-text-wrapper {
  transform-origin: left top;
  -webkit-padding-start: 0px;
  padding-inline-start: 0px;
  -webkit-padding-end: 0px;
  padding-inline-end: 0px;
  padding-top: 0px;
  padding-bottom: 0px;
  max-width: 100%;
}
:host-context([dir=rtl]):host(.textarea-label-placement-stacked) .label-text-wrapper, :host-context([dir=rtl]).textarea-label-placement-stacked .label-text-wrapper, :host-context([dir=rtl]):host(.textarea-label-placement-floating) .label-text-wrapper, :host-context([dir=rtl]).textarea-label-placement-floating .label-text-wrapper {
  transform-origin: right top;
}

@supports selector(:dir(rtl)) {
  :host(.textarea-label-placement-stacked) .label-text-wrapper:dir(rtl), :host(.textarea-label-placement-floating) .label-text-wrapper:dir(rtl) {
    transform-origin: right top;
  }
}

/**
 * Ensures the textarea does not
 * overlap the label.
 */
:host(.textarea-label-placement-stacked) textarea,
:host(.textarea-label-placement-floating) textarea,
:host(.textarea-label-placement-stacked[auto-grow]) .native-wrapper::after,
:host(.textarea-label-placement-floating[auto-grow]) .native-wrapper::after {
  -webkit-margin-start: 0px;
  margin-inline-start: 0px;
  -webkit-margin-end: 0px;
  margin-inline-end: 0px;
  margin-top: 8px;
  margin-bottom: 0px;
}

/**
 * This makes the label sit over the textarea
 * when the textarea is blurred and has no value.
 */
:host(.textarea-label-placement-floating) .label-text-wrapper {
  transform: translateY(100%) scale(1);
}

/**
 * The textarea should be hidden when the label
 * is on top of the textarea. This prevents the label
 * from overlapping any placeholder value.
 */
:host(.textarea-label-placement-floating) textarea {
  opacity: 0;
}

:host(.has-focus.textarea-label-placement-floating) textarea,
:host(.has-value.textarea-label-placement-floating) textarea {
  opacity: 1;
}

/**
 * This makes the label sit above the textarea.
 */
:host(.textarea-label-placement-stacked) .label-text-wrapper,
:host(.has-focus.textarea-label-placement-floating) .label-text-wrapper,
:host(.has-value.textarea-label-placement-floating) .label-text-wrapper {
  transform: translateY(50%) scale(0.75);
  /**
   * Label text should not extend
   * beyond the bounds of the textarea.
   */
  max-width: calc(100% / 0.75);
}

:host {
  --border-width: 0.55px;
  --border-color: var(--ion-item-border-color, var(--ion-border-color, var(--ion-color-step-250, #c8c7cc)));
  --padding-top: 10px;
  --padding-end: 0px;
  --padding-bottom: 8px;
  --padding-start: 0px;
  font-size: inherit;
}

:host(.legacy-textarea) {
  --padding-top: 10px;
  --padding-end: 8px;
  --padding-bottom: 10px;
  --padding-start: 0;
}

:host-context(.item-label-stacked),
:host-context(.item-label-floating) {
  --padding-top: 8px;
  --padding-bottom: 8px;
  --padding-start: 0px;
}

:host(.legacy-textarea) .native-textarea[disabled],
:host(.textarea-disabled) {
  opacity: 0.3;
}

:host(:not(.legacy-textarea)) {
  min-height: 44px;
}

/**
 * Since the label sits on top of the element,
 * the component needs to be taller otherwise the
 * label will appear too close to the textarea text.
 * Also, floating and stacked labels should not
 * push the label down since it it
 * sits on top of the textarea.
 */
:host(.textarea-label-placement-floating),
:host(.textarea-label-placement-stacked) {
  --padding-top: 0px;
  min-height: 56px;
}